import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchBoardKanbanThunk } from '../boardKanban/boardKanban.thunks'
import { removeBoard } from '../shared/boardCleanup.actions'
import { createInitialColumnPaginationItem } from './columnPagination.state.utils'
import { loadMoreColumnItemsThunk, reloadColumnItemsThunk } from './columnPagination.thunks'
import { refreshBoardItemsThunk } from '../boardKanban/boardKanban.refresh.thunks'

const columnPaginationSlice = createSlice({
  name: 'taskTrackerColumnPagination',
  initialState: {} as import('./columnPagination.types').ColumnPaginationSliceState,
  reducers: {
    resetColumnPagination(
      state,
      action: PayloadAction<{ boardId: number }>,
    ) {
      state[action.payload.boardId] = {}
    },
    syncColumnPaginationColumns(
      state,
      action: PayloadAction<{ boardId: number; columnIds: number[] }>,
    ) {
      const boardId = action.payload.boardId
      if (!state[boardId]) {
        state[boardId] = {}
      }
      const pagination = state[boardId]
      const columnIds = new Set(action.payload.columnIds)

      action.payload.columnIds.forEach((columnId) => {
        if (!pagination[columnId]) {
          pagination[columnId] = createInitialColumnPaginationItem()
        }
      })

      Object.keys(pagination).forEach((rawColumnId) => {
        const columnId = Number(rawColumnId)
        if (!columnIds.has(columnId)) {
          delete pagination[columnId]
        }
      })
    },
    setColumnPaginationItem(
      state,
      action: PayloadAction<{
        boardId: number
        columnId: number
        hasMore: boolean
        totalCount: number
      }>,
    ) {
      const { boardId, columnId, hasMore, totalCount } = action.payload
      if (!state[boardId]) {
        state[boardId] = {}
      }
      state[boardId][columnId] = {
        hasMore,
        totalCount,
        loadingMore: false,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state[action.payload.boardId]
      })
      .addCase(fetchBoardKanbanThunk.fulfilled, (state, action) => {
        const { boardId, details } = action.payload
        if (!state[boardId]) {
          state[boardId] = {}
        }

        const pagination = state[boardId]

        details.boardItems.forEach((item) => {
          if (item.type !== 'column') return

          pagination[item.id] = {
            hasMore: item.cardsHasMore ?? false,
            totalCount: item.cardsTotalCount ?? item.cardsCount ?? null,
            loadingMore: false,
          }
        })
      })
      .addCase(loadMoreColumnItemsThunk.pending, (state, action) => {
        const { boardId, columnId } = action.meta.arg
        if (!state[boardId]) {
          state[boardId] = {}
        }
        const pagination = state[boardId][columnId] ??
          createInitialColumnPaginationItem()
        state[boardId][columnId] = {
          ...pagination,
          loadingMore: true,
        }
      })
      .addCase(loadMoreColumnItemsThunk.fulfilled, (state, action) => {
        const { boardId, columnId, page } = action.payload
        if (!state[boardId]) {
          state[boardId] = {}
        }
        state[boardId][columnId] = {
          hasMore: page.hasMore,
          loadingMore: false,
          totalCount: page.totalCount,
        }
      })
      .addCase(loadMoreColumnItemsThunk.rejected, (state, action) => {
        const { boardId, columnId } = action.meta.arg
        if (!state[boardId]?.[columnId]) return
        state[boardId][columnId] = {
          ...state[boardId][columnId],
          loadingMore: false,
        }
      })
      .addCase(reloadColumnItemsThunk.fulfilled, (state, action) => {
        const { boardId, columnId, page } = action.payload
        if (!state[boardId]) {
          state[boardId] = {}
        }
        state[boardId][columnId] = {
          hasMore: page.hasMore,
          loadingMore: false,
          totalCount: page.totalCount,
        }
      })
      .addCase(refreshBoardItemsThunk.fulfilled, (state, action) => {
        const { boardId, result } = action.payload
        if (!state[boardId]) {
          state[boardId] = {}
        }

        result.columns.forEach((column) => {
          state[boardId][column.columnId] = {
            hasMore: column.hasMore,
            loadingMore: false,
            totalCount: column.totalCount,
          }
        })
      })
  },
})

export const { resetColumnPagination, syncColumnPaginationColumns, setColumnPaginationItem } =
  columnPaginationSlice.actions

export default columnPaginationSlice.reducer
